import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { ArrowLeft, Check, ShoppingBag } from 'lucide-react';

import { useCart } from '../contexts/CartContext';
import ClothingOrderWizard, {
  CartItem as WizardCartItem,
  CartItemPreview,
  ContactFormData,
  LogoPreviewData,
  WizardStep,
} from '../components/clothing/ClothingOrderWizard';
import ClothingHelpRequestForm from '../components/clothing/ClothingHelpRequestForm';
import ClothingConsultationBooker from '../components/clothing/ClothingConsultationBooker';
import { basketQuantity, CartItemEnquiry, submitClothingEnquiry } from '../lib/enquiry-service';
import { sendEnquiryEmails } from '../lib/email-service';

const colors = {
  accent: '#64a70b',
  dark: '#183028',
};

/**
 * The quote flow as its own screens rather than a modal over the shop: every
 * step has a URL, so Back works, a half-finished quote can be linked, and the
 * step is visible in analytics. OrderDrawer keeps the basket and sends people
 * here.
 *
 * The wizard's own steps are URL segments too, which is why it takes a
 * controlled `step` (see ClothingOrderWizard).
 */
type FlowStep = WizardStep | 'help-form' | 'consultation' | 'submitting' | 'success';

const SLUG_TO_STEP: Record<string, FlowStep> = {
  '': 'has-logo',
  logo: 'has-logo',
  upload: 'upload-logo',
  options: 'needs-help-options',
  preview: 'preview',
  details: 'contact-form',
  'design-help': 'help-form',
  consultation: 'consultation',
  sending: 'submitting',
  done: 'success',
};

const STEP_TO_SLUG: Record<FlowStep, string> = {
  'has-logo': 'logo',
  'upload-logo': 'upload',
  'needs-help-options': 'options',
  preview: 'preview',
  'contact-form': 'details',
  'help-form': 'design-help',
  consultation: 'consultation',
  submitting: 'sending',
  success: 'done',
};

const WIZARD_STEPS: FlowStep[] = ['has-logo', 'upload-logo', 'needs-help-options', 'preview', 'contact-form'];

const QuotePage: React.FC = () => {
  const navigate = useNavigate();
  const { step: stepSlug } = useParams<{ step?: string }>();
  const { cart } = useCart();

  const [enquiryRef, setEnquiryRef] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const step: FlowStep = SLUG_TO_STEP[stepSlug ?? ''] ?? 'has-logo';

  const goToStep = useCallback(
    (next: FlowStep, replace = false) => {
      navigate(`/quote/${STEP_TO_SLUG[next]}`, { replace });
    },
    [navigate]
  );

  // An unknown slug is not a 404 here, it is someone editing the URL: send them
  // to the start of the flow rather than showing nothing.
  useEffect(() => {
    if (stepSlug && !(stepSlug in SLUG_TO_STEP)) goToStep('has-logo', true);
  }, [stepSlug, goToStep]);

  // A quote is a quote for something. An empty basket mid-flow means the person
  // cleared it in another tab, so drop back to the start, which explains itself.
  useEffect(() => {
    if (cart.length === 0 && step !== 'success' && step !== 'has-logo') goToStep('has-logo', true);
  }, [cart.length, step, goToStep]);

  const firstCartItem = cart[0];
  const orderName = cart.length > 1 ? `${cart.length} items in your quote` : firstCartItem?.name || 'Your quote';
  const shortOrderName = cart.length > 1 ? `${cart.length} items` : firstCartItem?.name || 'Your quote';

  const cartItemsForWizard: WizardCartItem[] = useMemo(
    () =>
      cart.map(item => {
        const withColors = item as any;
        return {
          id: item.id,
          name: item.name,
          image: item.image,
          brand: item.brand,
          selectedColor: item.selectedColor,
          availableColors:
            withColors.availableColors?.map((c: any) => ({ name: c.name, rgb: c.rgb, image: c.image, code: c.code })) || [],
        };
      }),
    [cart]
  );

  const productColorsForWizard = useMemo(() => {
    const first = cart[0] as any;
    return (
      first?.availableColors?.map((c: any) => ({ name: c.name, rgb: c.rgb, image: c.image, code: c.code })) || []
    );
  }, [cart]);

  const initialColorIndex = useMemo(() => {
    const first = cart[0] as any;
    const idx = productColorsForWizard.findIndex(
      (c: any) => c.name === first?.selectedColor || c.code === first?.selectedColor
    );
    return idx >= 0 ? idx : 0;
  }, [productColorsForWizard, cart]);

  const enquiryCartItems: CartItemEnquiry[] = cart.map(item => ({
    id: item.id,
    name: item.name,
    styleCode: item.styleCode,
    color: item.selectedColor,
    size: item.selectedSize,
    image: item.image,
    quantity: item.quantity,
  }));

  const handleSelectPath = async (
    path: 'upload' | 'help' | 'consult',
    logoData?: LogoPreviewData,
    contactData?: ContactFormData,
    cartItemPreviews?: CartItemPreview[]
  ) => {
    if (path === 'upload' && contactData && logoData) {
      setSubmitError(null);
      goToStep('submitting', true);

      // The quantity box is hidden here because the basket already says how
      // many, so the basket total stands in for it.
      const estimatedQuantity = contactData.quantity.trim() || basketQuantity(enquiryCartItems);
      const contactExtras = {
        companyName: contactData.company,
        contactPreference: contactData.contactPreference,
        marketingOptIn: contactData.marketingOptIn,
        sendLogoLater: contactData.sendLogoLater,
        cartItems: enquiryCartItems,
      };

      try {
        const result = await submitClothingEnquiry({
          customerName: contactData.name,
          customerEmail: contactData.email,
          customerPhone: contactData.phone,
          ...contactExtras,
          productId: cart.length > 1 ? 'multi-item-order' : firstCartItem?.styleCode,
          productName: shortOrderName,
          productStyleCode: cart.length > 1 ? undefined : firstCartItem?.styleCode,
          productColor: firstCartItem?.selectedColor,
          productImageUrl: firstCartItem?.image,
          logoData: logoData.logoSrc,
          logoAnalysis: logoData.analysis,
          logoPositionX: logoData.x,
          logoPositionY: logoData.y,
          logoSizePercent: logoData.size,
          estimatedQuantity,
          additionalNotes: contactData.notes,
          enquiryType: 'upload',
          cartItemPreviews,
        });

        if (result.success && result.enquiryId && result.enquiryRef) {
          setEnquiryRef(result.enquiryRef);

          sendEnquiryEmails({
            customerEmail: contactData.email,
            customerName: contactData.name,
            customerPhone: contactData.phone,
            ...contactExtras,
            enquiryId: result.enquiryId,
            enquiryRef: result.enquiryRef,
            productName: shortOrderName,
            enquiryType: 'upload',
            estimatedQuantity,
            additionalNotes: contactData.notes,
            logoQuality: logoData.analysis?.qualityTier,
          }).catch(console.error);

          goToStep('success', true);
        } else {
          setSubmitError(result.error || 'Something went wrong. Please try again.');
          goToStep('contact-form', true);
        }
      } catch (error) {
        console.error('Submission error:', error);
        setSubmitError('Something went wrong. Please try again.');
        goToStep('contact-form', true);
      }
      return;
    }

    if (path === 'help') goToStep('help-form');
    if (path === 'consult') goToStep('consultation');
  };

  const isWizardStep = WIZARD_STEPS.includes(step);

  let content: React.ReactNode = null;

  if (isWizardStep) {
    content = (
      <ClothingOrderWizard
        step={step as WizardStep}
        onStepChange={next => goToStep(next)}
        productName={orderName}
        productImage={firstCartItem?.image}
        productColors={productColorsForWizard}
        initialColorIndex={initialColorIndex}
        onSelectPath={handleSelectPath}
        isMobile={isMobile}
        cartItems={cart.length > 1 ? cartItemsForWizard : []}
        hideQuantity={cart.length > 0}
      />
    );
  } else if (step === 'help-form') {
    content = (
      <ClothingHelpRequestForm
        productTitle={shortOrderName}
        onBack={() => goToStep('has-logo')}
        onComplete={() => goToStep('success', true)}
        isMobile={isMobile}
        hideQuantity={cart.length > 0}
        cartItems={enquiryCartItems}
      />
    );
  } else if (step === 'consultation') {
    content = (
      <ClothingConsultationBooker
        productName={shortOrderName}
        onBack={() => goToStep('has-logo')}
        onComplete={() => goToStep('success', true)}
        isMobile={isMobile}
      />
    );
  } else if (step === 'submitting') {
    content = (
      <div className="w-full max-w-xl mx-auto text-center py-16">
        <div className="w-16 h-16 mx-auto mb-6">
          <div
            className="w-full h-full rounded-full border-4 border-t-transparent animate-spin"
            style={{ borderColor: `${colors.accent} transparent ${colors.accent} ${colors.accent}` }}
          />
        </div>
        <h2 className="font-montserrat font-extrabold text-2xl text-white mb-2">Submitting your request…</h2>
        <p className="font-poppins text-white/60">Please wait while we process your enquiry</p>
      </div>
    );
  } else if (step === 'success') {
    content = (
      <div className="w-full max-w-xl mx-auto text-center py-12">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', delay: 0.2 }}
          className="w-24 h-24 rounded-full mx-auto mb-8 flex items-center justify-center"
          style={{ backgroundColor: `${colors.accent}25` }}
        >
          <Check className="w-12 h-12" style={{ color: colors.accent }} />
        </motion.div>

        <h2 className="font-montserrat font-extrabold text-3xl text-white mb-3">Request received</h2>

        {enquiryRef && (
          <div className="inline-block px-4 py-2 rounded-lg bg-white/10 border border-white/20 mb-4">
            <p className="font-poppins text-xs text-white/50 uppercase tracking-[0.14em] mb-1">Your reference</p>
            <p className="font-montserrat font-extrabold text-xl" style={{ color: colors.accent }}>
              {enquiryRef}
            </p>
          </div>
        )}

        <p className="font-poppins text-white/70 max-w-md mx-auto mb-8">
          Our team will review your request and get back to you within 24 hours with a mockup and quote.
          {enquiryRef && ' Keep your reference number handy for any enquiries.'}
        </p>

        <Link
          to="/clothing"
          className="inline-block px-6 py-3 rounded-xl font-poppins font-semibold text-black transition-all hover:opacity-90"
          style={{ backgroundColor: colors.accent }}
        >
          Continue browsing
        </Link>
      </div>
    );
  }

  const canLeave = step !== 'submitting';

  return (
    <div className="min-h-[calc(100svh-var(--header-h,0px))] w-full" style={{ backgroundColor: colors.dark }}>
      {/* Page texture, matching the modal this flow used to live in */}
      <div
        className="fixed inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage: 'url(/BlackTextureBackground.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-5xl px-4 py-6 md:px-8 md:py-10">
        <div className="flex items-center justify-between gap-4 mb-6">
          {canLeave ? (
            <Link
              to="/clothing"
              className="inline-flex items-center gap-2 font-poppins text-sm text-white/60 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to shop
            </Link>
          ) : (
            <span />
          )}

          {cart.length > 0 && step !== 'success' && (
            <span className="inline-flex items-center gap-2 font-poppins text-sm text-white/60">
              <ShoppingBag className="w-4 h-4" />
              {cart.length} {cart.length === 1 ? 'item' : 'items'} in this quote
            </span>
          )}
        </div>

        {cart.length === 0 && step === 'has-logo' ? (
          <div className="text-center py-20">
            <h1 className="font-montserrat font-extrabold text-3xl text-white mb-3">Your quote is empty</h1>
            <p className="font-poppins text-white/60 mb-8">Add a garment first and we'll take it from there.</p>
            <Link
              to="/clothing"
              className="inline-block px-6 py-3 rounded-xl font-poppins font-semibold text-black transition-all hover:opacity-90"
              style={{ backgroundColor: colors.accent }}
            >
              Browse clothing
            </Link>
          </div>
        ) : (
          <>
            {submitError && (
              <div className="mb-4 p-4 rounded-[12px] bg-red-500/10 border border-red-500/20 text-center">
                <p className="font-poppins text-sm text-red-400">{submitError}</p>
                <button
                  onClick={() => setSubmitError(null)}
                  className="font-poppins text-xs text-red-300 hover:text-red-200 mt-2 underline"
                >
                  Dismiss
                </button>
              </div>
            )}

            {content}
          </>
        )}
      </div>
    </div>
  );
};

export default QuotePage;
